<!--
 * Path: packages/frontend/src/core/components/app/AppMenubar.vue
 * Task: Top application menubar providing global actions, project switching, user account management, and sidebar toggling.
 * Update: Fixed severe syntax error in script setup preventing compilation (erroneous closing brackets).
-->
<template>
  <v-app-bar app color="surface" :density="authStore.isSidebarMinimized ? 'compact' : 'default'" elevation="1" class="px-2 transition-swing border-b">
    <div class="d-flex align-center">
      <!-- Menu Toggle -->
      <v-app-bar-nav-icon
        @click="authStore.toggleSidebar()"
        color="medium-emphasis"
        :density="authStore.isSidebarMinimized ? 'comfortable' : 'default'"
        :size="authStore.isSidebarMinimized ? 'small' : 'default'"
      ></v-app-bar-nav-icon>

      <div class="d-flex align-center ms-2 me-4">
        <!-- Logo (Clickable -> Home) -->
        <v-avatar :size="authStore.isSidebarMinimized ? 24 : 32" color="white" class="logo-avatar elevation-1" @click="uiStore.setActiveView('home')">
          <v-img :src="logoUrl" alt="Unravel"></v-img>
        </v-avatar>

        <!-- Dynamic Breadcrumbs -->
        <div class="d-flex align-center ms-4 font-weight-medium text-high-emphasis user-select-none breadcrumb-transition" :class="authStore.isSidebarMinimized ? 'text-subtitle-2' : 'text-h6'">
          <template v-for="(crumb, index) in breadcrumbs" :key="index">
            <span :class="{'text-medium-emphasis font-weight-regular': index < breadcrumbs.length - 1}">{{ crumb.title }}</span>
            <v-icon v-if="index < breadcrumbs.length - 1" size="small" class="mx-2 text-disabled">mdi-chevron-right</v-icon>
          </template>
        </div>
      </div>
    </div>

    <v-spacer></v-spacer>

    <!-- Right menu controls -->
    <div class="d-flex align-center" style="gap: 8px;">
      <!-- AI Chatbot Trigger (Strictly checks for valid string ID and authentication) -->
      <v-tooltip v-if="isAuthenticated && resolvedProjectId" location="bottom">
        <template v-slot:activator="{ props: tooltipProps }">
          <v-btn
            v-bind="tooltipProps"
            icon="mdi-robot-outline"
            variant="text"
            :color="uiStore.panels.showChatbot ? 'primary' : 'medium-emphasis'"
            class="hover-btn"
            :density="authStore.isSidebarMinimized ? 'comfortable' : 'default'"
            :size="authStore.isSidebarMinimized ? 'small' : 'default'"
            @click="uiStore.panels.showChatbot = !uiStore.panels.showChatbot"
          ></v-btn>
        </template>
        <span>Ask Unravel AI</span>
      </v-tooltip>

      <!-- Modular Search Modal Trigger (Only for authenticated users) -->
      <AppGlobalSearch
        v-if="isAuthenticated && resolvedProjectId"
        :density="authStore.isSidebarMinimized ? 'comfortable' : 'default'"
        :size="authStore.isSidebarMinimized ? 'small' : 'default'"
      />

      <!-- PROJECTS MENU -->
      <v-menu v-if="isAuthenticated" location="bottom end" offset="8">
        <template v-slot:activator="{ props }">
          <v-tooltip location="bottom">
            <template v-slot:activator="{ props: tooltipProps }">
              <v-btn
                v-bind="{...props, ...tooltipProps}"
                icon="mdi-folder-outline"
                variant="text"
                color="medium-emphasis"
                class="hover-btn"
                :density="authStore.isSidebarMinimized ? 'comfortable' : 'default'"
                :size="authStore.isSidebarMinimized ? 'small' : 'default'"
              ></v-btn>
            </template>
            <span>Projects</span>
          </v-tooltip>
        </template>
        <v-list density="compact" elevation="4" rounded="lg" class="py-2 border" width="320">
          <v-list-subheader class="text-caption text-medium-emphasis pb-0" style="min-height: 24px; line-height: 1;">Active Project</v-list-subheader>

          <v-list-item style="pointer-events: none;" class="mb-1">
            <template v-slot:prepend>
              <template v-if="resolvedProjectId">
                <EntityIcon :entity="{ icon: projectIcon, color: projectColor }" size="24" class="me-3" />
              </template>
              <template v-else>
                <v-icon size="24" class="me-3 text-medium-emphasis">mdi-folder-off-outline</v-icon>
              </template>
            </template>
            <v-list-item-title v-if="resolvedProjectId" class="text-body-1 font-weight-bold text-high-emphasis text-truncate">
              {{ projectTitle }}
            </v-list-item-title>
            <v-list-item-title v-else class="text-body-1 font-italic text-medium-emphasis">
              No active project
            </v-list-item-title>
          </v-list-item>

          <v-divider class="mb-2"></v-divider>

          <!-- AI Export Option -->
          <v-list-item link v-if="resolvedProjectId" @click="handleAiExport" :disabled="isExportingAi">
            <template v-slot:prepend>
              <v-icon size="20" class="me-3">mdi-database-export-outline</v-icon>
            </template>
            <v-list-item-title class="font-weight-medium">Export Context (Markdown)</v-list-item-title>
            <v-list-item-subtitle class="text-caption">Optimized for Gemini</v-list-item-subtitle>
            <template v-slot:append v-if="isExportingAi">
              <v-progress-circular indeterminate size="16" width="2" ></v-progress-circular>
            </template>
          </v-list-item>

          <v-divider class="mb-2"></v-divider>

          <v-list-item link v-if="isAuthenticated" @click="projectStore.showOpenProjectDialog()">
            <template v-slot:prepend><v-icon size="20" color="medium-emphasis" class="me-3">mdi-folder-open-outline</v-icon></template>
            <v-list-item-title>Open Project...</v-list-item-title>
          </v-list-item>

          <v-list-item link v-if="isAuthenticated" @click="handleOpenNewProjectDialog">
            <template v-slot:prepend><v-icon size="20" color="medium-emphasis" class="me-3">mdi-plus</v-icon></template>
            <v-list-item-title>New Project...</v-list-item-title>
          </v-list-item>

          <v-list-item link v-if="resolvedProjectId" @click="handleCloseProject">
            <template v-slot:prepend><v-icon size="20" color="medium-emphasis" class="me-3">mdi-close-circle-outline</v-icon></template>
            <v-list-item-title>Close Project</v-list-item-title>
          </v-list-item>

          <template v-if="authStore.user?.recentProjects && authStore.user.recentProjects.length > 0">
            <v-divider class="my-2"></v-divider>
            <v-list-subheader class="text-caption text-medium-emphasis pb-0" style="min-height: 24px; line-height: 1;">Recent Projects</v-list-subheader>
            <v-list-item link v-for="p in authStore.user.recentProjects" :key="p.id" @click="projectStore.openRecentProject(p)">
              <template v-slot:prepend>
                <EntityIcon :entity="{ icon: getRecentProjectIcon(p.id), color: getRecentProjectColor(p.id) }" size="20" class="me-3" />
              </template>
              <v-list-item-title>{{ p.title }}</v-list-item-title>
            </v-list-item>
          </template>
        </v-list>
      </v-menu>

      <!-- NEW ENTITY BUTTON (Primary Action) -->
      <v-menu offset-y v-if="canEditWorkspace && creatableTypes.length > 0">
        <template v-slot:activator="{ props }">
          <!-- Normal Mode: Pill Button with Text -->
          <v-btn
            v-if="!authStore.isSidebarMinimized"
            color="primary"
            variant="flat"
            class="text-none font-weight-bold ms-2 me-2 transition-swing"
            rounded="pill"
            v-bind="props"
          >
            <v-icon class="me-2">mdi-plus</v-icon>
            Create
          </v-btn>
          <!-- Compact Mode: Simple Icon Button -->
          <v-btn
            v-else
            color="primary"
            variant="flat"
            class="ms-2 me-2 transition-swing hover-btn"
            icon="mdi-plus"
            :density="'comfortable'"
            :size="'small'"
            v-bind="props"
          >
            <v-icon>mdi-plus</v-icon>
            <v-tooltip activator="parent" location="bottom">Create Entity</v-tooltip>
          </v-btn>
        </template>

        <v-list density="compact" elevation="4" rounded="lg" class="border">
          <v-list-subheader>Select type to create</v-list-subheader>
          <v-list-item link v-for="item in creatableTypes" :key="item.key" @click="createNewWithType(item.key)">
            <template v-slot:prepend>
              <EntityIcon :entity="{ entityType: item.key }" size="24" class="me-3" />
            </template>
            <v-list-item-title class="font-weight-medium">{{ item.title }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <!-- USER MENU -->
      <v-menu location="bottom end" offset="8">
        <template v-slot:activator="{ props }">
          <v-tooltip location="bottom">
            <template v-slot:activator="{ props: tooltipProps }">
              <v-btn
                v-bind="{...props, ...tooltipProps}"
                icon
                variant="text"
                class="hover-btn ms-1"
                :density="authStore.isSidebarMinimized ? 'comfortable' : 'default'"
                :size="authStore.isSidebarMinimized ? 'small' : 'default'"
              >
                <!-- Avatar shrinks slightly in compact mode -->
                <v-avatar :size="authStore.isSidebarMinimized ? 28 : 32" color="primary" variant="tonal" v-if="authStore.user">
                  <span class="text-primary font-weight-bold" :style="{ fontSize: authStore.isSidebarMinimized ? '12px !important' : '14px !important' }">
                    {{ safeUserInitial }}
                  </span>
                </v-avatar>
                <v-avatar :size="authStore.isSidebarMinimized ? 28 : 32" color="surface-variant" v-else>
                  <v-icon :size="authStore.isSidebarMinimized ? 16 : 20">mdi-account-outline</v-icon>
                </v-avatar>
              </v-btn>
            </template>
            <span>Account settings</span>
          </v-tooltip>
        </template>
        <v-list density="compact" elevation="4" rounded="lg" min-width="280" class="py-2 border">
          <v-list-subheader class="text-caption text-medium-emphasis pb-0" style="min-height: 24px; line-height: 1;">Current User</v-list-subheader>

          <div style="pointer-events: none;" class="mb-2 mt-1">
            <v-list-item class="py-1 min-height-0">
              <template v-slot:prepend><v-icon size="20" color="medium-emphasis" class="me-4">mdi-account-outline</v-icon></template>
              <v-list-item-title class="text-body-2 font-weight-bold text-high-emphasis text-truncate" :title="authStore.user?.email">
                {{ authStore.user?.name || authStore.user?.email || 'Guest User' }}
              </v-list-item-title>
            </v-list-item>
            <v-list-item class="py-1 min-height-0">
              <template v-slot:prepend><v-icon size="20" color="medium-emphasis" class="me-4">mdi-shield-account-outline</v-icon></template>
              <v-list-item-title class="text-caption text-high-emphasis">App: {{ appRoleDisplayName }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="projectRoleDisplayKey" class="py-1 min-height-0">
              <template v-slot:prepend><v-icon size="20" color="medium-emphasis" class="me-4">mdi-shield-star-outline</v-icon></template>
              <v-list-item-title class="text-caption text-high-emphasis">Project: {{ projectRoleDisplayName }}</v-list-item-title>
            </v-list-item>
          </div>

          <v-divider class="mb-2"></v-divider>

          <v-list-item link v-if="!isAuthenticated" @click="authStore.isLoginDialogOpen = true">
            <template v-slot:prepend><v-icon size="20" color="medium-emphasis" class="me-4">mdi-login</v-icon></template>
            <v-list-item-title>Log In</v-list-item-title>
          </v-list-item>

          <v-list-item link v-if="isAuthenticated" @click="authStore.isUserProfileDialogOpen = true">
            <template v-slot:prepend><v-icon size="20" color="medium-emphasis" class="me-4">mdi-account-cog-outline</v-icon></template>
            <v-list-item-title>User Profile</v-list-item-title>
          </v-list-item>

          <v-list-item @click.stop.prevent>
            <template v-slot:prepend><v-icon size="20" color="medium-emphasis" class="me-4">mdi-theme-light-dark</v-icon></template>
            <v-list-item-title>Design</v-list-item-title>
            <template v-slot:append>
              <div @click.stop.prevent>
                <v-btn-toggle
                  v-model="authStore.visibilityMode"
                  density="compact"
                  mandatory
                  color="primary"
                  @update:model-value="authStore.setVisibilityMode"
                  class="ms-2 border"
                  style="height: 28px;"
                >
                  <v-btn value="light" variant="text" title="Light Mode" min-width="36" class="px-0">
                    <v-icon size="small">mdi-white-balance-sunny</v-icon>
                  </v-btn>
                  <v-btn value="auto" variant="text" title="System Theme" min-width="36" class="px-0">
                    <v-icon size="small">mdi-brightness-auto</v-icon>
                  </v-btn>
                  <v-btn value="dark" variant="text" title="Dark Mode" min-width="36" class="px-0">
                    <v-icon size="small">mdi-weather-night</v-icon>
                  </v-btn>
                </v-btn-toggle>
              </div>
            </template>
          </v-list-item>

          <v-list-item link @click="authStore.isInfoDialogOpen = true">
            <template v-slot:prepend><v-icon size="20" color="medium-emphasis" class="me-4">mdi-information-outline</v-icon></template>
            <v-list-item-title>About Unravel</v-list-item-title>
          </v-list-item>

          <v-divider class="my-2"></v-divider>

          <v-list-item link v-if="isAuthenticated" @click="handleLogout">
            <template v-slot:prepend><v-icon size="20" color="error" class="me-4">mdi-logout</v-icon></template>
            <v-list-item-title class="text-error font-weight-medium">Log Out</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>
  </v-app-bar>

  <FloatingPanelChatbot v-model="uiStore.panels.showChatbot" />
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useAuthStore } from '@/store/auth';
import { useProjectStore } from '@/store/project';
import { useUiStore } from '@/store/ui';
import { useWorkspaceStore } from '@/store/workspace';
import { useEntityOperations } from '@/core/composables/useEntityOperations';
import { useAppStyle } from '@/core/composables/useAppStyle';
import { useSelectOptions } from '@/core/composables/useSelectOptions';
import logoUrl from '@/assets/unravel-logo-label-128.png';
import AppGlobalSearch from './AppGlobalSearch.vue';
import EntityIcon from '@/core/components/common/EntityIcon.vue';
import { usePermissions } from '@/core/composables/usePermissions';
import { AiExportService } from '@/core/services/AiExportService';
import FloatingPanelChatbot from '@/features/chat/FloatingPanelChatbot.vue';

const authStore = useAuthStore();
const projectStore = useProjectStore();
const uiStore = useUiStore();
const workspaceStore = useWorkspaceStore();

const { createNewEntity } = useEntityOperations();
const { getProjectTypeStyle } = useAppStyle();
const { entityTypeOptions, entityInfoMap } = useSelectOptions();
const { isAuthenticated, canEditWorkspace } = usePermissions();

const isExportingAi = ref(false);

// STRICT ID RESOLUTION: Defends against projectStore.loadedProject being an empty object {}
const resolvedProjectId = computed<string | null>(() => {
  if (!projectStore.loadedProject) return null;
  if (typeof projectStore.loadedProject === 'string' && projectStore.loadedProject.trim().length > 0) {
    return projectStore.loadedProject;
  }
  const obj = projectStore.loadedProject as any;
  if (obj && (obj.id || obj.value)) {
    return String(obj.id || obj.value);
  }
  return null;
});

const breadcrumbs = computed(() => {
  const rootMapping: Record<string, string> = {
    home: 'Home', dashboard: 'Dashboard', editor: 'Browser', mapWeb: 'Maps',
    mapSequence: 'Maps', mapIshikawa: 'Maps', kanban: 'Reports', matrix: 'Reports',
    impactMap: 'Reports', roledescription: 'Reports', calendar: 'Reports', customize: 'Project Settings',
    projectAdministration: 'App Settings', userSettings: 'App Settings', dataSettings: 'App Settings'
  };

  const secondaryMapping: Record<string, string> = {
    mapWeb: 'Web Map', mapSequence: 'Sequence Map', mapIshikawa: 'Ishikawa Diagram',
    kanban: 'Kanban Board', matrix: 'Matrix Report', impactMap: 'Impact Analysis',
    roledescription: 'Role Profiles', calendar: 'Calendar', projectAdministration: 'Projects',
    userSettings: 'Users', dataSettings: 'Data'
  };

  const path: { title: string }[] = [];

  if (projectStore.loadedProject?.title && uiStore.activeView !== 'home' && !uiStore.activeView.startsWith('projectAdministration') && !uiStore.activeView.startsWith('userSettings') && !uiStore.activeView.startsWith('dataSettings')) {
    path.push({ title: projectStore.loadedProject.title });
  }

  const rootTitle = rootMapping[uiStore.activeView] || 'Unravel';
  path.push({ title: rootTitle });

  const secondaryTitle = secondaryMapping[uiStore.activeView];
  if (secondaryTitle) path.push({ title: secondaryTitle });

  if (uiStore.activeTabTitle) path.push({ title: uiStore.activeTabTitle });

  const viewsWithEntityContext = ['editor', 'mapWeb', 'mapSequence', 'mapIshikawa', 'impactMap'];
  if (workspaceStore.selectedEntity && viewsWithEntityContext.includes(uiStore.activeView)) {
    path.push({ title: workspaceStore.selectedEntity.title || workspaceStore.selectedEntity.id });
  }

  return path;
});

const projectTitle = computed(() => {
  try {
    if (!resolvedProjectId.value) return 'Projects';
    if (projectStore.projectsMetadata && projectStore.projectsMetadata[resolvedProjectId.value]) {
      const meta = projectStore.projectsMetadata[resolvedProjectId.value];
      return meta.displayName || meta.title || resolvedProjectId.value;
    }
    return resolvedProjectId.value;
  } catch(e) { return 'Projects'; }
});

const projectIcon = computed(() => {
  try {
    if (!resolvedProjectId.value || !projectStore.projectsMetadata) return 'mdi-folder';
    const meta = projectStore.projectsMetadata[resolvedProjectId.value];
    if (!meta) return 'mdi-cube';
    return meta.icon || getProjectTypeStyle(meta.visibility || '').icon || 'mdi-cube';
  } catch(e) { return 'mdi-folder'; }
});

const projectColor = computed(() => {
  try {
    if (!resolvedProjectId.value || !projectStore.projectsMetadata) return 'primary';
    const meta = projectStore.projectsMetadata[resolvedProjectId.value];
    if (!meta) return 'primary';
    return meta.color || getProjectTypeStyle(meta.visibility || '').color || 'primary';
  } catch(e) { return 'primary'; }
});

// CRITICAL FIX: The arrow function was incorrectly terminated with "});" instead of "};"
const getRecentProjectIcon = (projectId: string) => {
  try {
    if (!projectStore.projectsMetadata) return 'mdi-folder-outline';
    const meta = projectStore.projectsMetadata[projectId];
    if (!meta) return 'mdi-cube';
    return meta.icon || getProjectTypeStyle(meta.visibility || '').icon || 'mdi-cube';
  } catch(e) { return 'mdi-folder-outline'; }
};

const getRecentProjectColor = (projectId: string) => {
  try {
    if (!projectStore.projectsMetadata) return 'medium-emphasis';
    const meta = projectStore.projectsMetadata[projectId];
    if (!meta) return 'medium-emphasis';
    return meta.color || getProjectTypeStyle(meta.visibility || '').color || 'primary';
  } catch(e) { return 'medium-emphasis'; }
};

const appRoleKey = computed<string>(() => {
  if (!authStore.user) return 'GUEST';
  return authStore.isAppAdmin ? 'APP_ADMIN' : 'REGISTERED_USER';
});

// Helper function to safely parse and lowercase arrays
const getSafeLowerArray = (arr: any): string[] => {
  if (Array.isArray(arr)) return arr.map(e => String(e).toLowerCase());
  if (typeof arr === 'string') return [arr.toLowerCase()];
  return [];
};

const projectRoleDisplayKey = computed<string | null>(() => {
  try {
    if (!resolvedProjectId.value || !projectStore.projectsMetadata || !authStore.user) return null;
    const meta = projectStore.projectsMetadata[resolvedProjectId.value];
    if (!meta) return null;
    if (authStore.isAppAdmin) return 'PROJECT_ADMIN';

    const email = typeof authStore.user.email === 'string' ? authStore.user.email.toLowerCase() : '';
    if (!email) return 'GUEST';

    const perms = meta.permissions || {};
    if (getSafeLowerArray(perms.projectAdmin).includes(email)) return 'PROJECT_ADMIN';
    if (getSafeLowerArray(perms.author).includes(email)) return 'AUTHOR';
    if (getSafeLowerArray(perms.reader).includes(email)) return 'READER';

    return 'GUEST';
  } catch (e) {
    console.error("Safely caught role computation error:", e);
    return 'GUEST';
  }
});

const appRoleDisplayName = computed<string>(() => {
  try {
    const systemRoles = authStore.appData?.userTypes?.systemRoles || {};
    if (appRoleKey.value === 'APP_ADMIN') return systemRoles.appAdmin?.name || 'App Administrator';
    else if (appRoleKey.value === 'REGISTERED_USER') return systemRoles.registeredUser?.name || 'Registered User';
    return systemRoles.guest?.name || 'Guest';
  } catch (e) { return 'Guest'; }
});

const projectRoleDisplayName = computed<string>(() => {
  try {
    if (!projectRoleDisplayKey.value) return '';
    const projectRoles = authStore.appData?.userTypes?.projectRoles || {};
    if (projectRoleDisplayKey.value === 'PROJECT_ADMIN') return projectRoles.projectAdmin?.name || 'Project Administrator';
    else if (projectRoleDisplayKey.value === 'AUTHOR') return projectRoles.author?.name || 'Author';
    else if (projectRoleDisplayKey.value === 'READER') return projectRoles.reader?.name || 'Reader';

    return (authStore.appData?.userTypes?.systemRoles || {}).guest?.name || 'Guest';
  } catch(e) { return 'Guest'; }
});

const safeUserInitial = computed(() => {
  try {
    return (authStore.user?.name || authStore.user?.email || '?').charAt(0).toUpperCase();
  } catch(e) { return '?'; }
});

const handleOpenNewProjectDialog = (): void => projectStore.showNewProjectDialog();

const handleCloseProject = (): void => {
  projectStore.setLoadedProject(null);
  uiStore.setActiveView('home');
};

const handleLogout = async (): Promise<void> => {
  await authStore.logout();
  projectStore.setLoadedProject(null);
  uiStore.setActiveView('home');
};

const handleAiExport = async (): Promise<void> => {
  if (!resolvedProjectId.value) return;

  isExportingAi.value = true;
  try {
    await AiExportService.exportProjectToMarkdown(resolvedProjectId.value);
    authStore.showNotification('AI Export generated successfully.', 'success');
  } catch (error: any) {
    authStore.showNotification(`Failed to export for AI: ${error.message}`, 'error');
  } finally {
    isExportingAi.value = false;
  }
};

const creatableTypes = computed<Array<{ key: string; title: string }>>(() => {
  try {
    return (entityTypeOptions.value || []).filter(item => !entityInfoMap.value?.[item.key]?.isArchived);
  } catch (e) { return []; }
});

const createNewWithType = (typeKey: string): void => createNewEntity(typeKey);
</script>

<style scoped>
.transition-swing {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1) !important;
}

.logo-avatar {
  cursor: pointer;
}

.breadcrumb-transition {
  transition: font-size 0.3s cubic-bezier(0.25, 0.8, 0.5, 1), line-height 0.3s cubic-bezier(0.25, 0.8, 0.5, 1) !important;
}

.min-height-0 {
  min-height: 0 !important;
}

.hover-btn:hover {
  background-color: rgba(var(--v-theme-on-surface), 0.04);
}
</style>
